import { listDatabaseApiKeys } from '@qdrant/qdrant-cloud-public-api/cluster/auth/v2/connect-query';
import { listBackupRestores, listBackups } from '@qdrant/qdrant-cloud-public-api/cluster/backup/v1/connect-query';
import {
  ClusterService,
  type ClusterValid,
  type ClusterNodeResourcesSummaryValid,
  type ClusterConfigurationValid,
  type ClusterNodeInfoValid,
  type DatabaseConfigurationValid,
  type ClusterNodeResourcesValid,
  type ListClustersResponseValid,
  type DatabaseConfiguration,
  type GetClusterResponseValid,
} from '@qdrant/qdrant-cloud-public-api/cluster/v1';
import {
  getCluster,
  getClusterDownscaleRisk,
  listClusters,
  createCluster,
  unsuspendCluster,
  restartCluster,
  deleteCluster,
  suggestClusterName,
  updateCluster,
  enableClusterJwtRbac,
} from '@qdrant/qdrant-cloud-public-api/cluster/v1/connect-query';
import { getClusterUsageMetrics } from '@qdrant/qdrant-cloud-public-api/monitoring/v1/connect-query';
import {
  listCloudProviderRegions,
  listCloudProviders,
} from '@qdrant/qdrant-cloud-public-api/platform/v1/connect-query';
import { transport } from '@/api/transport';
import {
  createServiceQueries,
  type DeepOmitProtobufInternals,
  type MutationFactory,
  queryOptions,
  createConnectQueryKey,
  useMutation,
} from '@/connect-query';
import { getOptimisticUpdatePhase } from './update-cluster-optimistic-phase';

// GRPC types
export type DatabaseConfigurationValidDto = DeepOmitProtobufInternals<DatabaseConfigurationValid>;
export type DatabaseConfigurationDto = DeepOmitProtobufInternals<DatabaseConfiguration>;
export type ClusterNodeResourcesDto = DeepOmitProtobufInternals<ClusterNodeResourcesValid>;
export type ClusterNodeResourcesSummaryDto = DeepOmitProtobufInternals<ClusterNodeResourcesSummaryValid>;
export type ClusterNodeInfoDto = DeepOmitProtobufInternals<ClusterNodeInfoValid>;
export type ClusterConfigurationDto = DeepOmitProtobufInternals<ClusterConfigurationValid>;

export const clusterService = createServiceQueries(ClusterService, {
  suggestClusterNameQuery: (params) => queryOptions(suggestClusterName, params, { transport, select: (d) => d.name }),
  listClustersQuery: (params) => {
    const queryOpts = queryOptions(listClusters, params, { transport });

    return {
      ...queryOpts,
      queryFn: async (p) => {
        const result = await queryOpts.queryFn(p);

        return result as ListClustersResponseValid & { items: ClusterValid[] };
      },
    };
  },
  getClusterDownscaleRiskQuery: (params) => ({
    ...queryOptions(getClusterDownscaleRisk, params, { transport }),
    // A downscale verdict is a live reading, so the app-wide 30 minute `staleTime` would let a
    // remount paint one from half an hour ago. On the query, not the call site: freshness is a
    // property of this RPC.
    staleTime: 0,
  }),
  getClusterQuery: (params) => {
    const queryOpts = queryOptions(getCluster, params, {
      transport,
    });

    return {
      ...queryOpts,
      queryFn: async (p) => {
        const result = await queryOpts.queryFn(p);

        return result as GetClusterResponseValid;
      },
    };
  },
});

export const useCreateClusterMutation: MutationFactory<typeof createCluster> = () =>
  useMutation(createCluster, {
    onSuccess: async (data, _variables, _onMutateResult, { client }) => {
      const { accountId } = data.cluster;
      await Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: suggestClusterName,
            input: { accountId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listClusters,
            input: { accountId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listCloudProviders,
            input: { accountId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listCloudProviderRegions,
            input: { accountId },
          }),
        }),
      ]);
    },
  });

export const useUnsuspendClusterMutation: MutationFactory<typeof unsuspendCluster> = () =>
  useMutation(unsuspendCluster, {
    onSuccess: (_data, { accountId, clusterId }, { router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listClusters,
            input: { accountId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getCluster,
            input: { accountId, clusterId },
          }),
        }),
        router.invalidate({
          filter: ({ routeId }) => routeId === '/_authenticated/_dashboard/accounts/$accountId',
        }),
      ]),
  });

export const useRestartClusterMutation: MutationFactory<typeof restartCluster> = () =>
  useMutation(restartCluster, {
    onSuccess: (_data, { accountId, clusterId }, { router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listClusters,
            input: { accountId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getCluster,
            input: { accountId, clusterId },
          }),
        }),
        router.invalidate({
          filter: ({ routeId }) => routeId === '/_authenticated/_dashboard/accounts/$accountId',
        }),
      ]),
  });

export const useDeleteClusterMutation: MutationFactory<typeof deleteCluster> = () =>
  useMutation(deleteCluster, {
    onSuccess: (_data, { accountId, clusterId, deleteBackups }, { router }, { client }) =>
      Promise.all([
        // The deleted cluster itself, like every other cluster mutation: the detail page and
        // `beforeLoad`'s `fetchQuery` both honor the 30min staleTime, so without this it served a
        // pre-delete phase until the next poll tick or a manual reload.
        // `refetchType: 'none'` only marks it stale: every surface that deletes navigates away from
        // the cluster, so refetching it here would race that navigation to GetCluster a record the
        // backend may already consider gone. A not-found is reported to Sentry (see errors.ts).
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getCluster,
            input: { accountId, clusterId },
          }),
          refetchType: 'none',
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listClusters,
            input: { accountId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listCloudProviders,
            input: { accountId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listCloudProviderRegions,
            input: { accountId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listBackupRestores,
            input: { accountId },
          }),
        }),
        // Only invalidate backups if the user chose to delete them
        ...(deleteBackups
          ? [
              client.invalidateQueries({
                queryKey: createConnectQueryKey({
                  schema: listBackups,
                  input: { accountId },
                }),
              }),
            ]
          : []),
        router.invalidate({
          filter: ({ routeId }) => routeId === '/_authenticated/_dashboard/accounts/$accountId',
        }),
      ]),
  });

export type ClusterDto = DeepOmitProtobufInternals<ClusterValid>;

export const useUpdateClusterMutation: MutationFactory<typeof updateCluster> = () =>
  useMutation(updateCluster, {
    onMutate: async ({ cluster, updateMask }, { client }) => {
      const { accountId, id } = cluster;
      const getClusterQueryKey = createConnectQueryKey({
        schema: getCluster,
        input: { accountId, clusterId: id },
      });

      await client.cancelQueries({ queryKey: getClusterQueryKey });

      const previousCluster = client.getQueryData<GetClusterResponseValid>(getClusterQueryKey);

      // Compute an optimistic phase for immediate UI feedback,
      // instead of waiting for the next polling cycle.
      const optimisticPhase = getOptimisticUpdatePhase(previousCluster?.cluster, cluster, updateMask);

      client.setQueryData<GetClusterResponseValid>(getClusterQueryKey, (old) => {
        if (!old?.cluster || !optimisticPhase || !old.cluster.state) {
          // Intentionally keep cached data unchanged when we cannot infer a reliable
          // optimistic phase; backend refetch in onSuccess will reconcile state.
          return old;
        }

        return {
          ...old,
          cluster: {
            ...old.cluster,
            state: {
              ...old.cluster.state,
              phase: optimisticPhase,
            },
          },
        };
      });

      return { previousCluster, getClusterQueryKey };
    },
    onError: (_error, _vars, onMutateResult, { client }) => {
      if (onMutateResult?.previousCluster && onMutateResult?.getClusterQueryKey) {
        client.setQueryData(onMutateResult.getClusterQueryKey, onMutateResult.previousCluster);
      }
    },
    onSuccess: (_data, { cluster }, { router }, { client }) => {
      const { accountId, id } = cluster;

      return Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getCluster,
            input: { accountId, clusterId: id },
          }),
          // Force an immediate refetch for active observers so optimistic
          // state is replaced by backend truth as soon as possible.
          refetchType: 'active',
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listClusters,
            input: { accountId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listCloudProviders,
            input: { accountId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listCloudProviderRegions,
            input: { accountId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getClusterUsageMetrics,
            input: { accountId, clusterId: id },
          }),
        }),
        router.invalidate({
          filter: ({ routeId }) => routeId === '/_authenticated/_dashboard/accounts/$accountId/clusters/$clusterId',
        }),
      ]);
    },
  });

export const useEnableJwtRbacMutation: MutationFactory<typeof enableClusterJwtRbac> = () =>
  useMutation(enableClusterJwtRbac, {
    onSuccess: (_data, { accountId, clusterId }, { router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listClusters,
            input: { accountId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getCluster,
            input: { accountId, clusterId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listDatabaseApiKeys,
            input: { accountId, clusterId },
          }),
        }),
        router.invalidate({
          filter: ({ routeId }) => routeId === '/_authenticated/_dashboard/accounts/$accountId',
        }),
      ]),
  });
